import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Zap, Clock4, Check, CreditCard } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";
import { useBranding } from "@/context/BrandingContext";

/* Razorpay checkout loader — defined once at module scope so it's idempotent
   across re-renders. */
function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const branding = useBranding();
    const logoH = Math.max(24, Math.min(64, Number(branding.logo_display_height) || 36));
    const layout = branding.logo_layout || "icon_and_text";
    const showIcon = layout !== "text_only";
    const showText = layout !== "icon_only";

    // ── form state ──────────────────────────────────────────────
    const [form, setForm] = useState({
        name: "", email: "", password: "", phone: "",
        firm_name: "", referral_code: "",
        referral_link_code: "",  // SM partner referral link attribution
        bar_council_enrollment_no: "",
        // Billing address — collected so the very first invoice the firm
        // receives has a correct "Bill to" block (mandatory under GST law
        // when we issue a tax invoice for the subscription / founder
        // one-time payment).
        address_line: "", pincode: "", city: "", state: "", country: "India",
    });
    const [loading, setLoading] = useState(false);
    const [pincodeLookup, setPincodeLookup] = useState({ status: "idle", note: "" }); // idle | loading | ok | err
    const [pendingAddons, setPendingAddons] = useState({}); // { code: qty }
    const [addonsCatalogue, setAddonsCatalogue] = useState([]);

    // ── pay-now state ───────────────────────────────────────────
    // The URL ?plan=X switches the page into "Subscribe Now" mode AND locks
    // the plan picker so the user can't change it (only the cycle).
    // When ?plan is absent we STILL default to "Subscribe Now" mode (free
    // chambers preselected) — the user explicitly clicks "14-day trial" if
    // they want the no-card path. This converts more visitors who arrive
    // ready-to-buy.
    const planFromUrl = (searchParams.get("plan") || "").toLowerCase();
    const isPayablePlan = planFromUrl && !["starter", "enterprise", ""].includes(planFromUrl);
    const isStarterUrl  = planFromUrl === "starter";
    // Default mode: trial only when the URL explicitly says ?plan=starter
    // (free tier), otherwise Subscribe Now.
    const [mode, setMode] = useState(isStarterUrl ? "trial" : "pay");
    const [cycle, setCycle] = useState("monthly");                     // 'monthly' | 'annual'
    const [plans, setPlans] = useState([]);                            // loaded from /plans/compare
    const [selectedPlanCode, setSelectedPlanCode] = useState(
        isPayablePlan ? planFromUrl : "chambers"
    );
    // When TRUE, the plan picker is locked to the URL-supplied plan and the
    // dropdown is hidden. Only the cycle toggle (and the form below) is shown.
    const planLocked = isPayablePlan;

    useEffect(() => {
        const ref      = searchParams.get("ref");
        const linkCode = searchParams.get("link_code");
        if (ref) setForm(f => ({ ...f, referral_code: ref.toUpperCase() }));
        // Store SM referral link code in component state for submission
        if (linkCode) setForm(f => ({ ...f, referral_link_code: linkCode.toUpperCase() }));
        try {
            const raw = localStorage.getItem("lawos_pending_addons");
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === "object") setPendingAddons(parsed);
            }
        } catch { /* ignore */ }
        api.get("/addons").then(r => setAddonsCatalogue(r.data || [])).catch(() => {});
        api.get("/plans/compare").then(r => {
            // The public /plans/compare endpoint already filters for active +
            // visible plans, so we only need to drop the free Starter and the
            // enterprise custom SKU. Founder (one-time) IS kept — when chosen
            // from the Founding Member card we lock it in and skip the cycle
            // toggle entirely.
            const list = (r.data?.plans || []).filter(p => {
                if (p.kind === "enterprise_custom") return false;
                if (p.code === "starter") return false;
                if (p.kind === "subscription") return Number(p.price_monthly_inr || 0) > 0;
                if (p.kind === "founder_one_time") return Number(p.one_time_price_inr || 0) > 0;
                return false;
            });
            setPlans(list);
        }).catch(() => {});
    }, [searchParams]);

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    // ── Pincode → city / state auto-fill ────────────────────────
    // Debounced so the user can finish typing. Uses our cached
    // /api/public/pincode/{pin} endpoint (India-Post upstream).
    useEffect(() => {
        const pin = (form.pincode || "").trim();
        if (!/^\d{6}$/.test(pin)) {
            setPincodeLookup({ status: "idle", note: "" });
            return;
        }
        let cancelled = false;
        setPincodeLookup({ status: "loading", note: "Resolving…" });
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/public/pincode/${pin}`);
                if (cancelled) return;
                setForm(f => ({
                    ...f,
                    // Only fill fields the user hasn't already typed a different value into,
                    // OR if the field is empty/equal-to-old-autofill.
                    city: data.city || f.city,
                    state: data.state || f.state,
                    country: data.country || f.country || "India",
                }));
                setPincodeLookup({ status: "ok", note: `${data.district || data.city}, ${data.state}` });
            } catch (err) {
                if (cancelled) return;
                setPincodeLookup({ status: "err", note: apiError(err) || "Pincode not found — fill city/state manually" });
            }
        }, 450);
        return () => { cancelled = true; clearTimeout(t); };
    }, [form.pincode]);

    const pendingList = Object.entries(pendingAddons)
        .map(([code, qty]) => {
            const a = addonsCatalogue.find(x => x.code === code);
            return a ? { ...a, qty } : null;
        })
        .filter(Boolean);
    const pendingTotal = pendingList.reduce((s, a) => s + (a.price_inr_per_month * a.qty), 0);

    // ── selected plan + price math ─────────────────────────────
    const selectedPlan = useMemo(
        () => plans.find(p => p.code === selectedPlanCode) || null,
        [plans, selectedPlanCode],
    );
    // Founder one-time plans are not cyclical — their price comes from
    // `one_time_price_inr` and we hide the Monthly/Annual picker for them.
    const isOneTime = selectedPlan?.kind === "founder_one_time";
    const cyclePrice = useMemo(() => {
        if (!selectedPlan) return 0;
        if (isOneTime) return Number(selectedPlan.one_time_price_inr || 0);
        return cycle === "annual"
            ? Number(selectedPlan.price_annual_inr || 0)
            : Number(selectedPlan.price_monthly_inr || 0);
    }, [selectedPlan, cycle, isOneTime]);
    const annualSavings = useMemo(() => {
        if (!selectedPlan || isOneTime) return 0;
        const mo = Number(selectedPlan.price_monthly_inr || 0) * 12;
        const yr = Number(selectedPlan.price_annual_inr || 0);
        return Math.max(0, mo - yr);
    }, [selectedPlan, isOneTime]);

    // Only the *Firm* tier plans actually have a firm — solo advocates,
    // chambers, founder-member buyers, and the free trial are individuals,
    // so we hide the Firm name input for them and silently derive a tenant
    // name from the user's own name (e.g. "Adv. Priya Mehra" → tenant name
    // "Adv. Priya Mehra"). The backend still needs *some* tenant name so we
    // never send a blank string.
    const requiresFirmName = mode === "pay" &&
        (selectedPlanCode === "firm_starter" || selectedPlanCode === "firm_pro");
    const effectiveFirmName = (requiresFirmName ? form.firm_name : form.name).trim();

    // ── 14-day trial submit ─────────────────────────────────────
    const submitTrial = async () => {
        await register({
            ...form,
            // Solo advocates / chambers / founder buyers don't fill in a firm
            // name — fall back to their own name so the tenant doc still has
            // a sensible label and slug.
            firm_name: effectiveFirmName,
        });
        if (pendingList.length > 0) {
            let attached = 0;
            for (const a of pendingList) {
                try {
                    await api.post("/tenant/addons", { addon_code: a.code, quantity: a.qty });
                    attached += 1;
                } catch { /* ignore */ }
            }
            try { localStorage.removeItem("lawos_pending_addons"); } catch { /* no-op */ }
            toast.success("Welcome to Law OS", { description: `Trial started · ${attached}/${pendingList.length} add-ons attached` });
        } else {
            toast.success("Welcome to Law OS", { description: "Your 14-day trial has started" });
        }
        navigate("/app");
    };

    // ── Pay-now submit (single API call + Razorpay modal) ──────
    const submitPay = async () => {
        const ok = await loadRazorpay();
        if (!ok) {
            toast.error("Could not load payment gateway. Please retry or use the 14-day trial.");
            return;
        }
        const { data } = await api.post("/auth/register-and-pay", {
            firm_name: effectiveFirmName,
            name: form.name,
            email: form.email,
            phone: form.phone,
            password: form.password,
            referral_code: form.referral_code || null,
            referral_link_code: form.referral_link_code || null,
            bar_council_enrollment_no: form.bar_council_enrollment_no || null,
            plan_code: selectedPlanCode,
            billing_cycle: isOneTime ? "monthly" : cycle,
            autopay: !isOneTime,
            address_line: form.address_line,
            pincode: form.pincode,
            city: form.city,
            state: form.state,
            country: form.country || "India",
        });
        // Razorpay Checkout SDK opens in *subscription* mode when
        // given a `subscription_id` and in *one-time* mode when given an
        // `order_id`. Pass exactly one — never both — based on which
        // path the backend chose.
        const checkoutOptions = {
            key: data.key_id,
            amount: data.amount_paise,
            currency: data.currency,
            name: "Law OS",
            description: isOneTime
                ? `${data.plan_name} · one-time`
                : (data.autopay_enabled
                    ? `${data.plan_name} · ${data.billing_cycle === "annual" ? "Annual" : "Monthly"} autopay`
                    : `${data.plan_name} · ${data.billing_cycle === "annual" ? "Annual" : "Monthly"} subscription`),
            theme: { color: "#D4AF37" },
            prefill: {
                name: data.prefill_name,
                email: data.prefill_email,
                contact: data.prefill_contact,
            },
            notes: {
                kind: "initial_subscription",
                subscription_id: data.subscription_id,
                tenant_id: data.tenant_id,
            },
            handler: async (resp) => {
                // Payment captured — webhook will activate the subscription.
                // Meanwhile, log the user in client-side so the dashboard is
                // ready when they get there.
                try {
                    localStorage.setItem("lawos_access_token", data.user_token);
                } catch { /* ignore localStorage write errors */ }
                toast.success(
                    data.autopay_enabled ? "Autopay set up · Welcome to Law OS" : "Welcome to Law OS",
                    { description: `Payment received · ₹${(data.amount_paise / 100).toLocaleString("en-IN")} for ${data.plan_name}` },
                );
                // Force-sync subscription state from Razorpay so the
                // Settings page reflects the live status without waiting
                // for the webhook (which usually lands within seconds).
                if (data.razorpay_subscription_id) {
                    try {
                        await api.post("/subscription/sync", {}, {
                            headers: { Authorization: `Bearer ${data.user_token}` },
                        });
                    } catch { /* webhook will land momentarily */ }
                }
                // Attach any carried-over add-ons (best effort)
                if (pendingList.length > 0) {
                    for (const a of pendingList) {
                        try { await api.post("/tenant/addons", { addon_code: a.code, quantity: a.qty }, { headers: { Authorization: `Bearer ${data.user_token}` } }); }
                        catch { /* ignore */ }
                    }
                    try { localStorage.removeItem("lawos_pending_addons"); } catch { /* no-op */ }
                }
                // Hard reload so AuthContext picks up the new token cleanly
                window.location.href = "/app";
            },
            modal: {
                ondismiss: () => {
                    toast.message("Payment cancelled", {
                        description: "Your account was created but is locked until payment completes. Sign in to retry or switch to a 14-day trial.",
                    });
                    navigate("/login");
                },
            },
        };
        // Attach exactly one of subscription_id (autopay) or order_id (one-time).
        if (data.razorpay_subscription_id) {
            checkoutOptions.subscription_id = data.razorpay_subscription_id;
        } else {
            checkoutOptions.order_id = data.order_id;
        }
        const rzp = new window.Razorpay(checkoutOptions);
        rzp.open();
    };

    const submit = async (e) => {
        e.preventDefault();
        if (mode === "pay" && !selectedPlan) {
            toast.error("Select a plan to continue");
            return;
        }
        // Billing address validation — required in *both* modes so every
        // tenant has a complete "Bill to" block on the very first invoice
        // they receive. The Pincode → city / state auto-fill keeps this
        // friction minimal.
        const missingAddr = [];
        if (!form.address_line.trim()) missingAddr.push("Address");
        if (!/^\d{6}$/.test((form.pincode || "").trim())) missingAddr.push("Pincode (6 digits)");
        if (!form.city.trim()) missingAddr.push("City");
        if (!form.state.trim()) missingAddr.push("State");
        if (missingAddr.length) {
            toast.error(`Please complete: ${missingAddr.join(", ")}`);
            return;
        }
        setLoading(true);
        try {
            if (mode === "pay") await submitPay();
            else                await submitTrial();
        } catch (e) {
            toast.error(apiError(e));
        } finally { setLoading(false); }
    };

    // ── render ──────────────────────────────────────────────────
    return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2.5 mb-8" data-testid="register-logo">
                    {showIcon && (branding.logo_src ? (
                        <img src={branding.logo_src} alt={branding.app_name}
                            style={{ height: logoH, width: "auto", maxWidth: logoH * 2.5 }}
                            className="object-contain"
                            onError={(e) => { e.target.style.display = "none"; }}/>
                    ) : (
                        <div style={{ height: logoH, width: logoH }}
                            className="border border-primary flex items-center justify-center shrink-0">
                            <span className="font-serif text-primary" style={{ fontSize: logoH * 0.45 }}>
                                {(branding.app_name || "L")[0]}
                            </span>
                        </div>
                    ))}
                    {showText && <div className="font-serif text-[15px]">{branding.app_name || "Law OS"}</div>}
                </Link>

                {/* Mode toggle — Trial vs Pay-now */}
                <div className="grid grid-cols-2 mb-6 rounded-lg overflow-hidden border border-border" role="tablist">
                    <button
                        type="button"
                        data-testid="register-mode-trial"
                        onClick={() => setMode("trial")}
                        className={`py-2.5 px-3 text-xs uppercase tracking-[0.16em] font-medium transition-colors flex items-center justify-center gap-1.5 ${
                            mode === "trial"
                                ? "bg-foreground text-background"
                                : "bg-card text-muted-foreground hover:text-foreground"
                        }`}>
                        <Clock4 className="w-3.5 h-3.5"/>
                        14-day trial
                    </button>
                    <button
                        type="button"
                        data-testid="register-mode-pay"
                        onClick={() => setMode("pay")}
                        className={`py-2.5 px-3 text-xs uppercase tracking-[0.16em] font-medium transition-colors flex items-center justify-center gap-1.5 ${
                            mode === "pay"
                                ? "bg-primary text-primary-foreground"
                                : "bg-card text-muted-foreground hover:text-foreground"
                        }`}>
                        <Zap className="w-3.5 h-3.5"/>
                        Subscribe now
                    </button>
                </div>

                <div className="overline mb-3 text-primary" data-testid="register-mode-label">
                    {mode === "pay" ? "Subscribe Now" : "Begin Trial"}
                </div>
                <h1 className="font-serif text-4xl font-light mb-2">
                    {mode === "pay" ? <>Open &amp; activate your account.</> : <>Open your chambers&apos; account.</>}
                </h1>
                <p className="text-muted-foreground text-sm mb-8">
                    {mode === "pay"
                        ? "Skip the trial. Pay once, subscription auto-renews, full access in seconds."
                        : "Fourteen days. Every module. No card required."}
                </p>

                {/* Plan + cycle picker — only when in pay mode */}
                {mode === "pay" && (
                    <div className="mb-6 rounded-xl border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="register-pay-summary">
                        {planLocked ? (
                            // Plan was chosen from /pricing card — lock it in,
                            // show a static label instead of the dropdown.
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Plan</span>
                                <span className="text-sm font-semibold text-foreground" data-testid="register-plan-locked">
                                    {selectedPlan?.name || selectedPlanCode}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Plan</span>
                                <select value={selectedPlanCode} onChange={(e) => setSelectedPlanCode(e.target.value)}
                                    data-testid="register-plan-select"
                                    className="bg-background border border-border focus:border-primary outline-none text-sm py-1.5 px-2 rounded-lg text-foreground font-medium">
                                    {plans.filter(p => p.kind === "subscription").map(p => (
                                        <option key={p.code} value={p.code}>{p.name} — ₹{p.price_monthly_inr}/mo</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Cycle toggle — hidden for one-time (founder) plans */}
                        {!isOneTime && (
                            <div className="grid grid-cols-2 gap-2">
                                <button type="button" onClick={() => setCycle("monthly")}
                                    data-testid="register-cycle-monthly"
                                    className={`text-xs py-2 px-2 rounded-lg border transition ${cycle === "monthly" ? "border-primary bg-primary text-primary-foreground font-semibold" : "border-border hover:border-primary/40"}`}>
                                    Monthly
                                </button>
                                <button type="button" onClick={() => setCycle("annual")}
                                    data-testid="register-cycle-annual"
                                    className={`text-xs py-2 px-2 rounded-lg border transition relative ${cycle === "annual" ? "border-primary bg-primary text-primary-foreground font-semibold" : "border-border hover:border-primary/40"}`}>
                                    Annual
                                    {annualSavings > 0 && (
                                        <span className={`absolute -top-2 -right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${cycle === "annual" ? "bg-background text-primary" : "bg-emerald-600 text-white"}`}>
                                            SAVE ₹{annualSavings.toLocaleString("en-IN")}
                                        </span>
                                    )}
                                </button>
                            </div>
                        )}

                        <div className="pt-2 border-t border-primary/20 flex items-baseline justify-between">
                            <span className="text-xs text-muted-foreground">
                                {isOneTime ? "One-time payment (excl. GST)" : "Charged today (excl. GST)"}
                            </span>
                            <span className="font-mono text-2xl font-bold text-primary" data-testid="register-pay-total">
                                ₹{cyclePrice.toLocaleString("en-IN")}
                            </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground leading-relaxed inline-flex items-start gap-1">
                            <Check className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0"/>
                            {isOneTime
                                ? <>One-time payment · 10-year platform access · no recurring charges.</>
                                : <>AutoPay enabled — renews automatically every {cycle === "annual" ? "year" : "month"}. Cancel anytime from Billing.</>
                            }
                        </p>
                    </div>
                )}

                {/* Carried-over add-ons from /pricing */}
                {pendingList.length > 0 && (
                    <div className="mb-6 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="register-pending-addons">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-primary"/>
                            <span className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">Add-ons carried over</span>
                        </div>
                        <ul className="space-y-1 text-sm text-foreground">
                            {pendingList.map(a => (
                                <li key={a.code} className="flex justify-between" data-testid={`pending-addon-${a.code}`}>
                                    <span>{a.name} {a.qty > 1 && <span className="text-muted-foreground">× {a.qty}</span>}</span>
                                    <span className="font-mono text-primary">₹{(a.price_inr_per_month * a.qty).toLocaleString("en-IN")}/mo</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-2 pt-2 border-t border-primary/20 flex justify-between text-xs">
                            <span className="text-muted-foreground">Add-on total {mode === "trial" ? "· billed from day 15" : "· first charge on next cycle"}</span>
                            <span className="font-mono text-primary font-semibold" data-testid="pending-addon-total">₹{pendingTotal.toLocaleString("en-IN")}/mo</span>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={submit} className="space-y-4">
                    {requiresFirmName ? (
                        <div data-testid="register-firm-field">
                            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Firm name</label>
                            <input required value={form.firm_name} onChange={upd("firm_name")} data-testid="register-firm-input"
                                placeholder="Mehra & Associates"
                                className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm" />
                            <p className="text-[10px] text-muted-foreground mt-1.5">
                                This is the name that appears on your tax invoice and across the platform.
                            </p>
                        </div>
                    ) : null}
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Your name</label>
                        <input required value={form.name} onChange={upd("name")} data-testid="register-name-input"
                            placeholder="Adv. Priya Mehra"
                            className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Email</label>
                            <input required type="email" value={form.email} onChange={upd("email")} data-testid="register-email-input"
                                className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Mobile</label>
                            <PhoneInput
                                value={form.phone}
                                onChange={(v) => setForm(f => ({ ...f, phone: v }))}
                                testid="register-phone"
                                placeholder="98765 43210"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Password</label>
                        <input required type="password" minLength="6" value={form.password} onChange={upd("password")} data-testid="register-password-input"
                            className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm" />
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">
                            Bar Council Enrollment No. <span className="opacity-50 normal-case tracking-normal">(optional)</span>
                        </label>
                        <input value={form.bar_council_enrollment_no} onChange={upd("bar_council_enrollment_no")} data-testid="register-bar-council-input"
                            placeholder="MH/1234/2018"
                            className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm font-mono" />
                    </div>

                    {/* Billing address — required for the GST tax invoice. Pincode
                        drives city / state auto-fill via /api/public/pincode. */}
                    <div className="pt-2 border-t border-border space-y-3" data-testid="register-billing-address">
                        <div className="flex items-center justify-between">
                            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block">Billing address</label>
                            <span className="text-[10px] text-muted-foreground">Required for tax invoice</span>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5 block">Address</label>
                            <input required value={form.address_line} onChange={upd("address_line")} data-testid="register-address-line"
                                placeholder="Office #, Building, Street, Locality"
                                className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5 block">Pincode</label>
                                <input required value={form.pincode} onChange={upd("pincode")} data-testid="register-pincode"
                                    inputMode="numeric" maxLength={6} pattern="\d{6}"
                                    placeholder="560001"
                                    className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-3 text-sm font-mono" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5 block">City</label>
                                <input required value={form.city} onChange={upd("city")} data-testid="register-city"
                                    placeholder="Bengaluru"
                                    className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-3 text-sm" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5 block">State</label>
                                <input required value={form.state} onChange={upd("state")} data-testid="register-state"
                                    placeholder="Karnataka"
                                    className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-3 text-sm" />
                            </div>
                        </div>
                        <div className="text-[10px] flex items-center justify-between" data-testid="register-pincode-status">
                            <span className={`${
                                pincodeLookup.status === "ok"      ? "text-emerald-600" :
                                pincodeLookup.status === "err"     ? "text-rose-600" :
                                pincodeLookup.status === "loading" ? "text-primary" :
                                "text-muted-foreground"
                            }`}>
                                {pincodeLookup.status === "ok"      && <>✓ {pincodeLookup.note}</>}
                                {pincodeLookup.status === "err"     && <>! {pincodeLookup.note}</>}
                                {pincodeLookup.status === "loading" && <>… resolving city / state from pincode</>}
                                {pincodeLookup.status === "idle"    && <>Enter pincode — city &amp; state auto-fill</>}
                            </span>
                            <span className="text-muted-foreground">{form.country || "India"}</span>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Referral code <span className="opacity-50 normal-case tracking-normal">(optional)</span></label>
                        <input value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} data-testid="register-referral-input"
                            placeholder="DEMOFDA5BA8"
                            className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm font-mono uppercase" />
                        {form.referral_code && <p className="text-[10px] text-primary mt-1">Your referrer will earn commission once you subscribe.</p>}
                    </div>
                    <button type="submit" disabled={loading} data-testid="register-submit-button"
                        className={`w-full py-3 font-medium disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2 ${
                            mode === "pay"
                                ? "bg-primary text-primary-foreground hover:opacity-90"
                                : "bg-foreground text-background hover:opacity-90"
                        }`}>
                        {loading
                            ? (mode === "pay" ? "Opening Razorpay…" : "Creating account…")
                            : (mode === "pay"
                                ? (<><CreditCard className="w-4 h-4"/> Subscribe &amp; pay ₹{cyclePrice.toLocaleString("en-IN")}</>)
                                : "Start 14-day trial")
                        }
                    </button>
                </form>

                <div className="mt-8 pt-6 border-t border-border flex justify-between text-sm">
                    <span className="text-muted-foreground">Already a member?</span>
                    <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">Sign in →</Link>
                </div>
            </div>
        </div>
    );
}
